import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  Grip,
  LayoutGrid,
  type LucideIcon,
  Plug,
  ServerCog,
  Settings,
  UsersRound,
  Wallet,
  Warehouse,
} from 'lucide-react';

import type { MenuSection, MenuSectionKey } from '@/types';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { cn } from '@/lib/utils';

import { useIsMobile } from '@/hooks';

type MainMenuProps = {
  sections: MenuSection[];
};

type SectionCard = {
  key: MenuSectionKey;
  label: string;
  description: string;
  icon: LucideIcon;
  href: string | null;
  isPlaceholder: boolean;
  isActive: boolean;
};

const isSectionActive = (section: MenuSection): boolean =>
  section.nav.some((item) => {
    if ('submenu' in item) {
      return item.submenu.some((sub) => sub.active === true);
    }
    return item.active === true;
  });

const SECTION_ICONS: Record<MenuSectionKey, LucideIcon> = {
  all: LayoutGrid,
  cash: Wallet,
  warehouse: Warehouse,
  integrations: Plug,
  settings: Settings,
  staff: UsersRound,
  reports: BarChart3,
  system_settings: ServerCog,
};

const SECTION_DESCRIPTIONS: Record<MenuSectionKey, string> = {
  all: 'Main workspace',
  cash: 'Daily cash operations',
  warehouse: 'Stock & supplies',
  integrations: 'Payment terminals',
  settings: 'Catalog & company configuration',
  staff: 'Staff & user logs',
  reports: 'Operations journal',
  system_settings: 'Owners & companies',
};

export const MainMenu = ({ sections }: MainMenuProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const cards = useMemo<SectionCard[]>(
    () =>
      sections.map((section) => ({
        key: section.key,
        label: section.label,
        description: SECTION_DESCRIPTIONS[section.key] ?? '',
        icon: SECTION_ICONS[section.key] ?? LayoutGrid,
        href: section.primary_href,
        isPlaceholder: !!section.placeholder,
        isActive: !section.placeholder && isSectionActive(section),
      })),
    [sections],
  );

  const renderCard = (card: SectionCard) => {
    const Icon = card.icon;
    const baseClass =
      'flex h-auto items-center justify-start gap-3 rounded-md p-3 text-left';
    const content = (
      <>
        <Icon className="size-7 shrink-0" />
        <div className="flex min-w-0 flex-col items-start gap-0.5">
          <span className="text-xl font-bold">{t(card.label)}</span>
          {card.description ? (
            <span className="text-xs">{t(card.description)}</span>
          ) : null}
        </div>
      </>
    );

    if (card.isPlaceholder || !card.href) {
      return (
        <div
          key={card.key}
          className={cn(baseClass, 'cursor-not-allowed opacity-50')}
          aria-disabled
        >
          {content}
        </div>
      );
    }

    return (
      <Button
        key={card.key}
        asChild
        variant="ghost"
        className={cn(
          baseClass,
          'cursor-pointer',
          'hover:text-main-menu-accent-foreground hover:bg-transparent',
          'dark:hover:text-main-menu-accent-foreground dark:hover:bg-transparent',
          'focus-visible:text-main-menu-accent-foreground focus-visible:bg-transparent',
          card.isActive && 'text-main-menu-accent-foreground',
        )}
      >
        <a href={card.href}>{content}</a>
      </Button>
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={t('Menu')}
          className="border-border bg-muted text-foreground hover:bg-muted/80 hover:text-foreground size-8 border focus:ring-0 focus:ring-offset-0"
        >
          <Grip className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        collisionPadding={8}
        className={cn(
          'dark bg-main-menu-background text-main-menu-foreground p-3',
          isMobile ? 'w-[calc(100vw-1rem)]' : 'w-80',
        )}
      >
        <div className="flex flex-col gap-1">
          {cards.map((card, index) => (
            <Fragment key={card.key}>
              {index > 0 ? <DropdownMenuSeparator className="my-1" /> : null}
              {renderCard(card)}
            </Fragment>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
